'use client';

import { useState, useEffect } from 'react';

import Button from './Button';
import LoadingSpinner from './LoadingSpinner';

import { useAppSelector } from '@/redux/hooks';
import { selectAccessToken, selectUserInfo } from '@/redux/auth/auth.selector';
import { useUpdateNewsletterStatusMutation } from '@/redux/auth/auth.api';

import { cn, safeParseInt } from '@/lib/utils';
import { NewsletterOption, IdentityStatus } from '@/redux/auth/auth.type';
import { locale } from '@/constants/locale';
import { safeLocalStorage } from '@/lib/safe-storage';

const NEWSLETTER_POPUP_KEY = 'newsletter_popup_dismissed';
const NEWSLETTER_POPUP_DISMISSALS = 'newsletter_popup_dismissals';
const NEWSLETTER_POPUP_NAVIGATIONS = 'newsletter_popup_navigations';
const NEWSLETTER_POPUP_LAST_URL = 'newsletter_popup_last_url';
const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
const MAX_DISMISSALS = 3;
const REQUIRED_NAVIGATIONS = 3;

const EXCLUDED_URL_PATTERNS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-pending',
  '/otp-verify',
  '/identity-verify',
  '/change-password',
  '/complete-profile',
  '/verify-work-email',
  '/deal/',
  '/retailer/'
];

interface NewsletterPopupProps {
  className?: string;
}

const isUrlExcluded = (url: string): boolean => {
  return EXCLUDED_URL_PATTERNS.some((pattern) => {
    if (pattern.endsWith('/')) {
      return url.startsWith(pattern);
    }
    return url === pattern;
  });
};

export default function NewsletterPopup({ className }: NewsletterPopupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [navigationCount, setNavigationCount] = useState(0);

  const accessToken = useAppSelector(selectAccessToken);
  const userInfo = useAppSelector(selectUserInfo);
  const [updateNewsletterStatus] = useUpdateNewsletterStatusMutation();

  useEffect(() => {
    if (!accessToken || !userInfo?.email) {
      return;
    }

    const trackNavigation = () => {
      if (typeof window === 'undefined') return;

      const currentUrl = window.location.pathname;
      const lastUrl = safeLocalStorage.getItem(NEWSLETTER_POPUP_LAST_URL);

      if (lastUrl && lastUrl !== currentUrl) {
        const currentNavigations = safeParseInt(
          safeLocalStorage.getItem(NEWSLETTER_POPUP_NAVIGATIONS),
          0
        );
        const newNavigations = currentNavigations + 1;
        safeLocalStorage.setItem(
          NEWSLETTER_POPUP_NAVIGATIONS,
          newNavigations.toString()
        );
        setNavigationCount(newNavigations);
      }

      safeLocalStorage.setItem(NEWSLETTER_POPUP_LAST_URL, currentUrl);
    };

    trackNavigation();

    const handlePopState = () => {
      // Use requestIdleCallback for non-critical tracking to avoid blocking INP
      if ('requestIdleCallback' in window) {
        requestIdleCallback(trackNavigation, { timeout: 200 });
      } else {
        setTimeout(trackNavigation, 100);
      }
    };

    window.addEventListener('popstate', handlePopState);

    // INP optimized: Use requestIdleCallback for history tracking
    if (typeof window !== 'undefined' && window.history) {
      const originalPushState = window.history.pushState;
      const originalReplaceState = window.history.replaceState;

      window.history.pushState = function (...args) {
        originalPushState.apply(window.history, args);
        if ('requestIdleCallback' in window) {
          requestIdleCallback(trackNavigation, { timeout: 200 });
        } else {
          setTimeout(trackNavigation, 100);
        }
      };

      window.history.replaceState = function (...args) {
        originalReplaceState.apply(window.history, args);
        if ('requestIdleCallback' in window) {
          requestIdleCallback(trackNavigation, { timeout: 200 });
        } else {
          setTimeout(trackNavigation, 100);
        }
      };

      // Store originals for cleanup
      return () => {
        window.removeEventListener('popstate', handlePopState);
        window.history.pushState = originalPushState;
        window.history.replaceState = originalReplaceState;
      };
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [accessToken, userInfo?.email]);

  useEffect(() => {
    if (!accessToken || !userInfo?.email || userInfo?.isAllowNewsLetter) {
      return;
    }

    const isVerified =
      userInfo?.workIdentityVerificationStatus === IdentityStatus.VERIFIED ||
      userInfo?.workIdentityVerificationStatus ===
        IdentityStatus.MANUAL_VERIFIED;

    if (!isVerified) {
      return;
    }

    const currentUrl = window.location.pathname;
    if (isUrlExcluded(currentUrl)) {
      return;
    }

    const dismissals = safeParseInt(
      safeLocalStorage.getItem(NEWSLETTER_POPUP_DISMISSALS),
      0
    );
    if (dismissals >= MAX_DISMISSALS) {
      return;
    }

    if (navigationCount < REQUIRED_NAVIGATIONS) {
      return;
    }

    const dismissedTime = safeLocalStorage.getItem(NEWSLETTER_POPUP_KEY);
    if (dismissedTime) {
      const timeDiff = Date.now() - safeParseInt(dismissedTime, 0);
      if (timeDiff < FOURTEEN_DAYS_MS) {
        return;
      }
    }

    const timer = setTimeout(() => {
      setIsOpen(true);
      setTimeout(() => setIsVisible(true), 10);
    }, 1000);

    return () => clearTimeout(timer);
  }, [
    accessToken,
    userInfo?.email,
    userInfo?.isAllowNewsLetter,
    userInfo?.workIdentityVerificationStatus,
    navigationCount
  ]);

  const handleSubscribe = async () => {
    setIsLoading(true);

    try {
      await updateNewsletterStatus({
        status: NewsletterOption.subscribe,
        method: 'newsletter_popup'
      }).unwrap();
      setIsOpen(false);
      setIsVisible(false);
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsVisible(false);

    const currentDismissals = safeParseInt(
      safeLocalStorage.getItem(NEWSLETTER_POPUP_DISMISSALS),
      0
    );
    safeLocalStorage.setItem(
      NEWSLETTER_POPUP_DISMISSALS,
      (currentDismissals + 1).toString()
    );
    safeLocalStorage.setItem(NEWSLETTER_POPUP_KEY, Date.now().toString());
  };

  if (!isOpen) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-50">
      {/* CLS-optimized: Use only GPU-accelerated transform and opacity, no position changes */}
      <div
        className={cn(
          'bg-popover pointer-events-auto fixed bottom-0 left-1/2 w-[95vw] max-w-120 -translate-x-1/2 rounded-t-2xl shadow-2xl transition-[transform,opacity] duration-500 ease-out will-change-transform',
          'sm:bottom-8 sm:w-[90vw] sm:max-w-130 sm:rounded-2xl',
          'md:max-w-145',
          'lg:max-w-155',
          'shadow-[0_-4px_20px_rgba(0,0,0,0.15)] sm:shadow-[0_8px_32px_rgba(0,0,0,0.12)]',
          isVisible
            ? 'translate-y-0 opacity-100'
            : 'translate-y-full opacity-0 sm:translate-y-4',
          className
        )}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 rounded-full p-1 opacity-70 transition-opacity hover:opacity-100"
        >
          <svg
            className="size-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          <span className="sr-only">{locale.newsletterPopup.closeButton}</span>
        </button>

        <div className="p-6 sm:p-8">
          <div className="mb-6 text-center">
            <h2 className="font-manrope text-foreground mb-3 text-xl font-bold sm:text-2xl">
              {locale.newsletterPopup.headline}
            </h2>
            <p className="font-manrope text-foreground/80 text-sm/relaxed sm:text-base">
              {locale.newsletterPopup.description}
            </p>
          </div>

          <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
            <Button
              onClick={handleSubscribe}
              disabled={isLoading}
              className="bg-brand-yellow font-manrope hover:bg-brand-yellow/90 focus:ring-brand-yellow/50 flex-1 rounded-full px-6 py-3 text-sm font-semibold text-black focus:ring-2 sm:px-8"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <LoadingSpinner />
                  <span className="ml-2">
                    {locale.newsletterPopup.subscribing}
                  </span>
                </div>
              ) : (
                locale.newsletterPopup.subscribeButton
              )}
            </Button>

            <Button
              onClick={handleClose}
              disabled={isLoading}
              variant="secondary"
              className="border-input font-manrope text-foreground hover:bg-muted/60 focus:ring-input/50 bg-popover flex-1 rounded-full border px-6 py-3 text-sm font-semibold focus:ring-2 sm:px-8"
            >
              {locale.newsletterPopup.declineButton}
            </Button>
          </div>

          <p className="font-manrope text-muted-foreground mt-4 text-center text-xs">
            {locale.newsletterPopup.imprint}
          </p>
        </div>
      </div>
    </div>
  );
}
